import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Trans } from 'react-i18next';

import { Rail, RailContainer } from 'components/Rail';
import MidresponsiveAd from 'components/Ad/Midresponsive';

import { LAYOUT_7_LEFT_COLUMNS_AT_LARGE_BREAKPOINT } from 'lib/brandFeatures';
import Breakpoints from 'lib/Breakpoints';
import BTE from 'lib/BTE';
import { LayoutContext } from 'lib/ContextTypes';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { hasSingleNav } from 'lib/hasSingleNav';
import {
  getFirstPackageFromPackages,
  getPkgTypesInZone,
  packageHasTitleOrType,
} from 'lib/packageHelper';
import { stub as $t } from '@nbcnews/analytics-framework';

import getPackagesForZone from '../getPackagesForZone';

import './styles.themed.scss';

const LATEST_STORIES_TRACKING_ID = 'mbt_rightRailTabbed_latestClick';
/**
 * Functional component that renders the right rail tabbed layout
 * @param {object} props
 * @param {Array} [props.packages=[]]
 * @returns {React.ReactElement} The rendered RightRailTabbed component
 */
const RightRailTabbed = ({ packages = [] }) => {
  const [contentScroll, setContentScroll] = useState(0);
  const [contentTab, setContentTab] = useState(true);
  const [railScroll, setRailScroll] = useState(0);
  const [railTab, setRailTab] = useState(false);
  const [tabbed, setTabbed] = useState(true);

  const containerRef = useRef(null);

  /**
   * Sets to tabbed on mobile, small, and medium breakpoints
   */
  const setTabs = () => {
    setTabbed(!Breakpoints.isLorXL());
  };

  /**
   * Handles resize events to update tab states
   */
  const onResize = () => {
    setTabs();
  };

  /**
   * Sets the initial scroll positions for content and rail tabs
   */
  const setInitialTabScroll = () => {
    const containerTop = containerRef.current.offsetTop - 50;
    setContentScroll(containerTop);
    setRailScroll(containerTop);
  };

  useEffect(() => {
    setTabs();

    setInitialTabScroll();

    $t('register', LATEST_STORIES_TRACKING_ID);

    BTE.monitorResize();

    BTE.on('resize', onResize);
  }, []);

  useEffect(() => {
    if (!tabbed) {
      return;
    }

    if (contentTab) {
      window.scrollTo(0, contentScroll);
    }

    if (railTab) {
      window.scrollTo(0, railScroll);
    }
  }, [tabbed, contentScroll, contentTab, railTab, railScroll]);

  /**
   * Changes the active tab and updates scroll positions accordingly
   * @param {string} tab - The target tab ('content', or 'rail')
   */
  const changeTab = (tab) => {
    const { pageYOffset: scrollY } = window;

    if (tab === 'content') {
      setContentTab(true);
      setRailTab(false);
      setRailScroll(scrollY);
    } else {
      $t('track', LATEST_STORIES_TRACKING_ID);
      setContentTab(false);
      setRailTab(true);
      setContentScroll(scrollY);
    }
  };

  /**
   * Gets the title of the first teaseList
   * @returns {*|string} The title of the teaseList package or 'Latest'
   */
  const tabTitle = () => {
    const teaseList = packages.find((pkg) => pkg.type && pkg.type === 'teaseList');

    if (teaseList?.metadata?.title) {
      return teaseList.metadata.title;
    }

    return 'Latest';
  };

  /**
   * Renders tabs
   * @returns {React.ReactElement|null}
   */
  const tabs = () => {
    const showContentTab = !tabbed || (tabbed && contentTab);
    const showRailTab = !tabbed || (tabbed && railTab);

    const isWaffle = packages.some((pkg) => pkg.type && pkg.type === 'waffle');
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    if (isWaffle || !packages.length) {
      return null;
    }

    return (
      <div
        className={classNames(
          'right-rail-tabbed__tabs',
          { 'right-rail-tabbed__tabs--visible': tabbed },
        )}
        data-testid="right-rail-tabbed__tabs"
      >
        <div
          className={classNames(
            'right-rail-tabbed__tab',
            'js-left-tab',
            { 'right-rail-tabbed__tab--active': showContentTab },
          )}
          data-testid="right-rail-tabbed__content-tab"
          onClick={() => { changeTab('content'); }}
        >
          <Trans>Top Stories</Trans>
        </div>
        <div
          className={classNames(
            'right-rail-tabbed__tab',
            'js-right-tab',
            { 'right-rail-tabbed__tab--active': showRailTab },
          )}
          data-testid="right-rail-tabbed__rail-tab"
          onClick={() => { changeTab('rail'); }}
        >
          {tabTitle()}
        </div>
      </div>
    );
    /* eslint-enable jsx-a11y/click-events-have-key-events */
    /* eslint-enable jsx-a11y/no-static-element-interactions */
  };


  const context = useContext(LayoutContext);
  const { vertical } = context;

  const has7LeftColumnsAtLargeBreakpoint = getFeatureConfigForBrand(
    LAYOUT_7_LEFT_COLUMNS_AT_LARGE_BREAKPOINT,
    vertical,
  );

  const pkgTypesInRail = getPkgTypesInZone(packages, 2);

  const showContentTab = !tabbed || (tabbed && contentTab);
  const showRailTab = !tabbed || (tabbed && railTab);

  const firstPackageHasTitleOrIsPancake = packageHasTitleOrType(
    getFirstPackageFromPackages(packages),
    'pancake',
  );

  const containerClasses = classNames('layout-grid-container', {
    'right-rail-tabbed__has-title-is-pancake':
      showContentTab && firstPackageHasTitleOrIsPancake,
  });
  const leftClasses = classNames(
    'right-rail-tabbed__container--left layout-grid-item',
    {
      'grid-col-7-l grid-col-8-xl': has7LeftColumnsAtLargeBreakpoint,
      'grid-col-8-l grid-col-9-xl': !has7LeftColumnsAtLargeBreakpoint,
    },
  );
  const rightClasses = classNames(
    'layout-grid-item',
    {
      'grid-col-5-l grid-col-4-xl': has7LeftColumnsAtLargeBreakpoint,
      'grid-col-4-l grid-col-3-xl': !has7LeftColumnsAtLargeBreakpoint,
    },
  );

  const tabClassNames = classNames(
    'layout-rightRailTabbed',
    {
      small: hasSingleNav(vertical),
    },
  );

  return (
    <div className={tabClassNames} ref={containerRef}>
      {tabs()}
      <RailContainer className={containerClasses}>
        {showContentTab && (
          <div className={leftClasses} data-testid="right-rail-tabbed__content-tab-contents">
            {getPackagesForZone(1, packages, { ...context, isRailAdjacent: true })}
          </div>
        )}
        {showRailTab && (
          <Rail className={rightClasses} packageTypes={pkgTypesInRail}>
            {getPackagesForZone(2, packages, { ...context, railContext: 'right' })}
          </Rail>
        )}
      </RailContainer>

      <MidresponsiveAd />
    </div>
  );
};

export default RightRailTabbed;
